import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { useT } from '@/i18n/LanguageContext';

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const acceptInvite = useAuthStore((s) => s.acceptInvite);
  const t = useT();

  const token = searchParams.get('token') || '';
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <AuthLayout>
        <h1 className="text-2xl font-bold text-[#222222] mb-2">{t.invite.invalidTitle}</h1>
        <p className="text-sm text-[#9A9A9A] mb-4">
          {t.invite.invalidMessage}
        </p>
        <Link to="/login" className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors">
          {t.invite.goToLogin}
        </Link>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim() || !email.trim() || !password.trim()) {
      setError(t.invite.fieldsRequired);
      return;
    }
    if (password.length < 8) {
      setError(t.invite.passwordMinLength);
      return;
    }
    setLoading(true);
    const result = await acceptInvite(
      token,
      fullName.trim(),
      email.trim(),
      password.trim(),
      phone.trim() || undefined,
    );
    setLoading(false);
    if (result === true) {
      navigate('/', { replace: true });
    } else {
      setError(result);
    }
  };

  return (
    <AuthLayout>
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#523EF7] mb-4">
        {t.common.appName}
      </p>

      <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight mb-2">
        {t.invite.title}
      </h1>
      <p className="text-[#9A9A9A] text-base mb-8">
        {t.invite.subtitle}
      </p>

      <div className="max-w-sm mx-auto">
        <form onSubmit={handleSubmit} className="space-y-4 text-left">
          <div className="flex flex-col gap-1">
            <label htmlFor="full-name" className="text-sm font-medium text-[#3F3F3F]">
              {t.invite.fullName} <span className="text-red-500">*</span>
            </label>
            <input
              id="full-name"
              placeholder={t.invite.fullNamePlaceholder}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoFocus
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-sm font-medium text-[#3F3F3F]">
              {t.common.email} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder={t.invite.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-sm font-medium text-[#3F3F3F]">
              {t.common.password} <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              placeholder={t.invite.passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="auth-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="phone" className="text-sm font-medium text-[#3F3F3F]">
              {t.invite.phoneLabel}
            </label>
            <input
              id="phone"
              type="tel"
              placeholder={t.invite.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="auth-input"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="auth-btn-primary w-full"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              t.invite.submit
            )}
          </button>

          <div className="text-center pt-1">
            <Link
              to="/login"
              className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors"
            >
              {t.invite.hasAccount}
            </Link>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
